import { useState, useRef } from "react";
import { UseFormReturn } from "react-hook-form";
import { FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Label } from "@/components/ui/label";
import { useVendors } from "@/hooks/useVendors";
import { usePerkCategories } from "@/hooks/usePerkCategories";
import { usePerkLocations } from "@/hooks/usePerkLocations";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { X, Upload } from "lucide-react";
import { PhotoUploader } from "@/components/ui/PhotoUploader";
import { PhotoParentType } from "@/types/photos";
import { cn } from "@/lib/utils";
import type { PerkWizardFormData } from "./PerkWizard";
import type { CmsPerkResponse } from "@/types/generated/cms.generated";

interface BasicInfoStepProps {
  form: UseFormReturn<PerkWizardFormData>;
  existingPerk?: CmsPerkResponse;
  onPhotoSelect?: (files: File[]) => void;
}

const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

export function BasicInfoStep({ form, existingPerk, onPhotoSelect }: BasicInfoStepProps) {
  const { vendors = [], isLoading: vendorsLoading } = useVendors();
  const { data: categories = [], isLoading: categoriesLoading } = usePerkCategories();
  const { data: locations = [], isLoading: locationsLoading } = usePerkLocations();
  const [localPreviewUrl, setLocalPreviewUrl] = useState<string | null>(null);
  const [localFileName, setLocalFileName] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleLocalFileSelect = (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const validFile = fileArray.find(
      (f) => ACCEPTED_IMAGE_TYPES.includes(f.type) && f.size <= MAX_FILE_SIZE_BYTES
    );
    if (validFile) {
      // Revoke previous preview URL to avoid memory leaks
      if (localPreviewUrl) {
        URL.revokeObjectURL(localPreviewUrl);
      }
      const previewUrl = URL.createObjectURL(validFile);
      setLocalPreviewUrl(previewUrl);
      setLocalFileName(validFile.name);
      onPhotoSelect?.([validFile]);
    }
  };

  const handleRemoveLocalFile = () => {
    if (localPreviewUrl) {
      URL.revokeObjectURL(localPreviewUrl);
    }
    setLocalPreviewUrl(null);
    setLocalFileName(null);
    onPhotoSelect?.([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const selectedCategoryIds = form.watch("categoryIds") || [];
  const selectedLocationIds = form.watch("locationIds") || [];

  const toggleCategory = (id: string) => {
    const current = form.getValues("categoryIds") || [];
    if (current.includes(id)) {
      form.setValue("categoryIds", current.filter((c) => c !== id));
    } else {
      form.setValue("categoryIds", [...current, id]);
    }
  };

  const toggleLocation = (id: string) => {
    const current = form.getValues("locationIds") || [];
    if (current.includes(id)) {
      form.setValue("locationIds", current.filter((l) => l !== id));
    } else {
      form.setValue("locationIds", [...current, id]);
    }
  };

  // Flatten locations for display
  const flatLocations = locations.flatMap((loc) => {
    const result = [loc];
    if (loc.children) {
      result.push(...loc.children);
    }
    return result;
  });

  return (
    <div className="space-y-6">
      <FormField
        control={form.control}
        name="title"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Title</FormLabel>
            <FormControl>
              <Input placeholder="e.g. 20% Off Gym Membership" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="description"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Description</FormLabel>
            <FormControl>
              <Textarea
                placeholder="Describe the perk benefit for employees..."
                className="min-h-[100px]"
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="vendorId"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Vendor</FormLabel>
            {vendorsLoading ? (
              <Skeleton className="h-10 w-full" />
            ) : (
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a vendor" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {vendors.map((vendor) => (
                    <SelectItem key={vendor.id} value={vendor.id || ""}>
                      {vendor.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <FormMessage />
          </FormItem>
        )}
      />

      <div className="grid grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="startDate"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Start Date</FormLabel>
              <FormControl>
                <Input type="date" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="endDate"
          render={({ field }) => (
            <FormItem>
              <FormLabel>End Date</FormLabel>
              <FormControl>
                <Input type="date" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      {/* Categories multi-select */}
      <div className="space-y-2">
        <Label>Categories</Label>
        {categoriesLoading ? (
          <Skeleton className="h-10 w-full" />
        ) : (
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => {
              const isSelected = selectedCategoryIds.includes(cat.id || "");
              return (
                <Badge
                  key={cat.id}
                  variant={isSelected ? "default" : "outline"}
                  className="cursor-pointer"
                  onClick={() => toggleCategory(cat.id || "")}
                >
                  {cat.name}
                  {isSelected && <X className="w-3 h-3 ml-1" />}
                </Badge>
              );
            })}
          </div>
        )}
        <p className="text-xs text-muted-foreground">Click to toggle categories</p>
      </div>

      {/* Locations multi-select */}
      <div className="space-y-2">
        <Label>Locations</Label>
        {locationsLoading ? (
          <Skeleton className="h-10 w-full" />
        ) : (
          <div className="flex flex-wrap gap-2">
            {flatLocations.map((loc) => {
              const isSelected = selectedLocationIds.includes(loc.id || "");
              return (
                <Badge
                  key={loc.id}
                  variant={isSelected ? "default" : "outline"}
                  className="cursor-pointer"
                  onClick={() => toggleLocation(loc.id || "")}
                >
                  {loc.name}
                  {loc.locationType === "Country" && loc.countryCode ? ` (${loc.countryCode})` : ""}
                  {isSelected && <X className="w-3 h-3 ml-1" />}
                </Badge>
              );
            })}
          </div>
        )}
        <p className="text-xs text-muted-foreground">Click to toggle locations</p>
      </div>

      {/* Photo upload */}
      <div className="space-y-2">
        <Label>Perk Image (Optional)</Label>
        {existingPerk?.id ? (
          <>
            <PhotoUploader
              parentType={PhotoParentType.Perk}
              parentId={existingPerk.id}
              maxPhotos={1}
              maxFileSizeMB={10}
              onUploadComplete={(_photoIds, photoUrls) => {
                if (photoUrls && photoUrls.length > 0 && photoUrls[0]) {
                  form.setValue("imageUrl", photoUrls[0]);
                }
              }}
            />
            {existingPerk.imageUrl && (
              <div className="flex items-center gap-2 mt-2">
                <img
                  src={existingPerk.imageUrl}
                  alt="Current perk image"
                  className="w-16 h-16 rounded object-cover border"
                />
                <p className="text-xs text-muted-foreground">Current image</p>
              </div>
            )}
          </>
        ) : (
          <>
            <div
              className={cn(
                "border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer hover:border-primary/50",
                localPreviewUrl ? "border-primary/30 bg-primary/5" : "border-border"
              )}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); }}
              onDrop={(e) => {
                e.preventDefault();
                if (e.dataTransfer.files.length > 0) {
                  handleLocalFileSelect(e.dataTransfer.files);
                }
              }}
            >
              {localPreviewUrl ? (
                <div className="flex flex-col items-center gap-3">
                  <img
                    src={localPreviewUrl}
                    alt="Selected preview"
                    className="max-h-32 rounded-lg object-cover"
                  />
                  <div className="flex items-center gap-2">
                    <p className="text-sm text-muted-foreground">{localFileName}</p>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveLocalFile();
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">Click or drag to replace</p>
                </div>
              ) : (
                <>
                  <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-sm font-medium">Drag and drop an image, or click to browse</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    JPEG, PNG, or WebP, up to {MAX_FILE_SIZE_MB}MB
                  </p>
                </>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_IMAGE_TYPES.join(",")}
                className="hidden"
                onChange={(e) => {
                  if (e.target.files && e.target.files.length > 0) {
                    handleLocalFileSelect(e.target.files);
                  }
                }}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              The image will be uploaded after the perk is created.
            </p>
          </>
        )}
      </div>

      <FormField
        control={form.control}
        name="isFeatured"
        render={({ field }) => (
          <FormItem className="flex items-center space-x-2">
            <FormControl>
              <Switch checked={field.value} onCheckedChange={field.onChange} />
            </FormControl>
            <FormLabel>Featured Perk</FormLabel>
            <FormDescription>Featured perks appear prominently in the employee app</FormDescription>
          </FormItem>
        )}
      />
    </div>
  );
}
